import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function Leaderboard({ token }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("all"); // all, month, week

  useEffect(() => {
    fetchLeaderboard();
  }, [timeframe]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/leaderboard?timeframe=${timeframe}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (err) {
      setError("Failed to load leaderboard");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    switch(rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `#${rank}`;
    }
  };

  const getProgressBar = (score, maxScore) => {
    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    return (
      <div style={{
        width: "100%",
        height: "8px",
        background: "#333",
        borderRadius: "4px",
        overflow: "hidden"
      }}>
        <div style={{
          width: `${percentage}%`,
          height: "100%",
          background: "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)",
          borderRadius: "4px",
          transition: "width 0.5s ease"
        }}></div>
      </div>
    );
  };

  return (
    <div className="card" data-testid="leaderboard-section">
      <h3 style={{ color: "#FFD700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
        🏆 Student Leaderboard
      </h3>

      {/* Timeframe Selector */}
      <div style={{ 
        display: "flex", 
        gap: "10px", 
        marginBottom: "20px",
        flexWrap: "wrap"
      }}>
        {[
          { value: "week", label: "This Week" },
          { value: "month", label: "This Month" },
          { value: "all", label: "All Time" }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => setTimeframe(option.value)}
            data-testid={`timeframe-${option.value}`}
            style={{
              padding: "10px 20px",
              background: timeframe === option.value ? "#FFD700" : "#333",
              color: timeframe === option.value ? "#000" : "#eee",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: timeframe === option.value ? "bold" : "normal",
              transition: "all 0.2s"
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#aaa" }}>
          <div style={{ fontSize: "2rem", marginBottom: "10px" }}>⏳</div>
          Loading leaderboard...
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#ff6b6b" }}>
          {error}
        </div>
      ) : leaderboard.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#aaa" }}>
          <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📊</div>
          No data yet. Complete quizzes to appear on the leaderboard!
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {leaderboard.map((student, index) => (
            <div
              key={student.user_id || index}
              data-testid={`leaderboard-rank-${index + 1}`}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "15px 20px",
                background: index < 3 
                  ? `linear-gradient(135deg, ${index === 0 ? 'rgba(255,215,0,0.15)' : index === 1 ? 'rgba(192,192,192,0.15)' : 'rgba(205,127,50,0.15)'} 0%, rgba(30,30,30,1) 100%)`
                  : "#1a1a1a",
                borderRadius: "10px",
                border: index < 3 
                  ? `2px solid ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}`
                  : "1px solid #333",
                transition: "transform 0.2s",
                gap: "15px"
              }}
            >
              {/* Rank */}
              <div style={{
                fontSize: index < 3 ? "1.8rem" : "1.2rem",
                fontWeight: "bold",
                minWidth: "50px",
                textAlign: "center",
                color: index === 0 ? "#FFD700" : index === 1 ? "#C0C0C0" : index === 2 ? "#CD7F32" : "#888"
              }}>
                {getMedalEmoji(index + 1)}
              </div>

              {/* Student Info */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: "bold",
                  color: "#eee",
                  fontSize: "1.1rem",
                  marginBottom: "5px"
                }}>
                  {student.username}
                  {student.ordained && <span style={{ marginLeft: "8px", color: "#90EE90" }}>✝️</span>}
                </div>
                <div style={{ marginTop: "8px" }}>
                  {getProgressBar(student.total_score, leaderboard[0]?.total_score || 100)}
                </div>
              </div>

              {/* Stats */}
              <div style={{ textAlign: "right", minWidth: "120px" }}>
                <div style={{ 
                  fontSize: "1.5rem", 
                  fontWeight: "bold", 
                  color: "#FFD700" 
                }}>
                  {student.total_score?.toLocaleString() || 0}
                </div>
                <div style={{ 
                  fontSize: "0.8rem", 
                  color: "#888",
                  marginTop: "3px"
                }}>
                  {student.quizzes_completed || 0} quizzes • {student.avg_score || 0}% avg
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={{
        marginTop: "25px",
        padding: "15px",
        background: "#111",
        borderRadius: "8px",
        fontSize: "0.85rem",
        color: "#888"
      }}>
        <strong style={{ color: "#FFD700" }}>How points are calculated:</strong>
        <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
          <li>Quiz completed: +10 points</li>
          <li>Quiz passed (70%+): +25 bonus points</li>
          <li>Perfect score (100%): +50 bonus points</li>
          <li>Day streak bonus: +5 points per day</li>
        </ul>
      </div>

      <style>{`
        .card:hover [data-testid^="leaderboard-rank-"]:hover {
          transform: translateX(5px);
        }
      `}</style>
    </div>
  );
}

export default Leaderboard;
